import { ChangeDetectionStrategy, Component, effect, input, output } from '@angular/core';
import type { GridSelectionMode } from '../../core/interfaces/grid-config.types';
import type { GridPlugin, GridPluginAttachContext } from '../../core/interfaces/grid-context.types';
import { GRID_PLUGIN } from '../../core/tokens/grid-plugin.token';

/**
 * Plugin de seleção avançada. É um Component (não uma Directive pura) pela mesma razão de
 * `<company-column>`: é usado como tag própria (`<company-grid-selection-plugin />`).
 * Recebe Store/Bridge/EventBus via `attach()` chamado explicitamente pelo Host — não via
 * `inject()` no construtor (ver adaptação de DI no relatório da POC).
 */
@Component({
  selector: 'company-grid-selection-plugin',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
  providers: [{ provide: GRID_PLUGIN, useExisting: CompanyGridSelectionPlugin }],
})
export class CompanyGridSelectionPlugin<T = unknown> implements GridPlugin {
  readonly pluginId = 'selection';

  readonly mode = input<GridSelectionMode>('multiple');
  readonly persistentSelection = input(true);
  readonly selection = input<readonly T[]>([]);
  readonly selectionChange = output<readonly T[]>();

  private context: GridPluginAttachContext | null = null;
  private unsubscribe: (() => void) | null = null;

  constructor() {
    effect(() => {
      const mode = this.mode();
      this.context?.store.setSelectionMode(mode);
      this.context?.bridge.setSelectionMode(mode);
    });
  }

  attach(context: GridPluginAttachContext): void {
    this.detach();
    this.context = context;
    context.store.setSelectionMode(this.mode());
    context.bridge.setSelectionMode(this.mode());

    this.unsubscribe = context.eventBus.on('SELECTION_CHANGED', event => {
      const rows = event.selectedRows as readonly T[];
      const current = this.selection();
      if (current.length === rows.length && current.every((row, index) => row === rows[index])) {
        return;
      }
      this.selectionChange.emit(rows);
    });
  }

  detach(): void {
    this.unsubscribe?.();
  }
}
